import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Box, Typography, Divider } from '@mui/material';
import routeNames from '../../../../routes/routeNames';
import { CustomButton, CustomReactSelect } from '../../../shared';
import { DownloadIcon, PieChart } from '../../../../assets';
import { useDispatch, useSelector } from 'react-redux';
import { CSVLink } from 'react-csv';
import { getPhysiciansList } from '../../../../redux/thunks/physiciansThunk';
import { actions as companyActions } from '../../../../redux/reducers/companyReducer';
import { actions } from '../../../../redux/reducers/physicianReducer';

const PhysiciansInfoBar = (props) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {} = props;
    const { company, companyOptions } = useSelector((state) => state.company);
    const { totalPhysicians, totalPages } = useSelector(
        (state) => state.physician,
    );
    const [csvData, setCSVData] = useState([]);
    const [isDownloading, setIsDownloading] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const {
        userDetails: { is_admin },
    } = useSelector((state) => state.user);
    const { setCompany } = companyActions;
    const cancelRequestRef = useRef(false);

    const hideTargetListAnalysis =
        [66, 64, 140, 161].includes(company.id) && !is_admin;
    const hideExportData = [66, 64, 140, 161].includes(company.id) && !is_admin;

    const handleCompanyChange = (option) => {
        cancelRequestRef.current = true;
        dispatch(setCompany(option));
        setIsDownloading(true);
        setCSVData([]);
        dispatch(actions.resetToInitialState());
    };

    const handleTargetListAnalysis = () => {
        navigate(routeNames.PHYSICIAN_ANALYSIS);
    };

    const headers = [
        { label: 'NPI', key: 'npi' },
        { label: 'Name', key: 'name' },
        { label: 'Location', key: 'location' },
        { label: 'Channel Preference', key: 'channel' },
        { label: 'Partner Preference', key: 'partner' },
        { label: 'Physician Segment', key: 'physicianSegment' },
    ];

    const handleCSVData = async () => {
        const csvDataArray = [];
        const count = Math.ceil(totalPhysicians / 5000);
        setIsLoading(true);
        for (let page = 0; page <= count; page++) {
            const position = page * 5000 - 5000;
            try {
                const result = await dispatch(
                    getPhysiciansList({
                        companyId: company.id,
                        position: position,
                        page_size: 5000,
                        type: 'download',
                    }),
                );
                const data = result.payload?.results?.physicians;
                if (data && data.length) {
                    data.forEach((physician) => {
                        const {
                            npi_id,
                            first_name,
                            last_name,
                            address1,
                            city,
                            state,
                            zip,
                            district,
                            channel_preference,
                            partner_preference,
                            segmentation,
                        } = physician;

                        let export_segmentation = JSON.stringify(
                            segmentation || [],
                        );
                        export_segmentation = export_segmentation
                            .replace(/[\[\]']+/g, '')
                            .replace(/['"]+/g, '');

                        const datum_export = {
                            npi: npi_id,
                            name: `${first_name} ${last_name}`,
                            location: `${address1} ${city} ${state} ${zip} ${'District:' + district}`,
                            channel: channel_preference,
                            partner: Array.isArray(partner_preference)
                                ? partner_preference.join(', ')
                                : partner_preference,
                            physicianSegment: export_segmentation,
                        };
                        csvDataArray.push(datum_export);
                    });
                }
            } catch (error) {
                if (!cancelRequestRef.current) {
                    console.error('API call failed', error);
                }
            }
        }
        if (!cancelRequestRef.current && csvDataArray.length > 0) {
            setCSVData(csvDataArray);
            setIsDownloading(false);
        }
        setIsLoading(false);
    };

    const hidePhysicianPageHeaderTotalCount = () => {
        const hideFrom = [3];
        return hideFrom.includes(company.id);
    };

    useEffect(
        () => () => {
            cancelRequestRef.current = true;
        },
        [],
    );

    return (
        <>
            <Grid className="flex-row-space-between mt-20px">
                <Grid className="d-flex gap-12" xs={6}>
                    {/* Select Inputs */}
                    <Box className="flex-row gap-12">
                        {is_admin && (
                            <Box width={300}>
                                <CustomReactSelect
                                    customStyle={{ borderRadius: '50px' }}
                                    handleFilter={handleCompanyChange}
                                    options={companyOptions}
                                    placeholder="Select Company"
                                    selectedOption={{
                                        label: company.name,
                                        value: company.id,
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                </Grid>
                <Grid className="d-flex gap-12" xs={6}>
                    {hideTargetListAnalysis ? null : (
                        <CustomButton
                            borderColor="#FAEDF9"
                            buttonColor="#FAEDF9"
                            handleClick={handleTargetListAnalysis}
                            icon={<PieChart color="#8C3F7D" />}
                            title="Target List Analysis"
                            titleColor="#8C3F7D"
                        />
                    )}

                    {hideExportData ? null : isDownloading ? (
                        <CustomButton
                            buttonColor="#AD509E"
                            handleClick={() => handleCSVData()}
                            icon={<DownloadIcon />}
                            title={
                                isLoading ? 'Downloading...' : 'Export All Data'
                            }
                            titleColor="#FFFFFF"
                        />
                    ) : (
                        <CSVLink
                            data={csvData}
                            filename="Target List.csv"
                            headers={headers}
                        >
                            <CustomButton
                                buttonColor="#AD509E"
                                icon={<DownloadIcon />}
                                title="Ready For Export"
                                titleColor="#FFFFFF"
                            />
                        </CSVLink>
                    )}
                </Grid>
            </Grid>
            {!hidePhysicianPageHeaderTotalCount() && (
                <>
                    <Grid className="mt-32px" xs={6}>
                        <Box className="flex-column gap-4">
                            <Box>
                                <Typography className="text-500fw-18fs-28lh">
                                    Target List
                                </Typography>
                                <Typography className="text-400fw-14fs-20lh-bg-gray-500">
                                    Total Physicians: {totalPhysicians}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                    <Divider className="border-1-gray" />
                </>
            )}
        </>
    );
};

export default PhysiciansInfoBar;
